import { createAsyncThunk, createSlice, current } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';
import sessionApis from '../../../api/services/session.service';
import transcriptionApi from '../../../api/services/transcription.service';
import {
  decorateTrialPayload,
  findSiblingTrials,
  getEffectivePhase,
  isSiblingPhaseKey,
  isTrialKeyForRun,
  makeTrialKey,
  shapeRegularTrials,
  shapeTaskAnalysisTrials,
  TrialSaveDecoration,
} from './trialUtils';

interface TranscriptionSegment {
  id: string;
  text: string;
  timestamp: string;
  sessionTime: number;
}

export type RequestStatus = 'idle' | 'loading' | 'loaded' | 'error';

// One cache entry per (sessionRunId, effectivePhase, targetId) — see makeTrialKey.
export interface TrialEntry {
  data: any[];
  currentTrial: number;
  status: RequestStatus;
  fetchedAt: number | null;
  // Guards against a stale fulfilled landing after a newer force-refetch.
  requestId: string | null;
  // In-flight save/undo count; drives the per-card saving spinner.
  saving: number;
  hasJustUndone: boolean;
  error?: string;
}

const createTrialEntry = (): TrialEntry => ({
  data: [],
  currentTrial: 1,
  status: 'idle',
  fetchedAt: null,
  requestId: null,
  saving: 0,
  hasJustUndone: false,
});

const ensureEntry = (state: any, key: string): TrialEntry => {
  if (!state.value.trials[key]) {
    state.value.trials[key] = createTrialEntry();
  }
  return state.value.trials[key];
};

const initialState = {
  value: {
    targets: [],
    // Phase the current `targets` array was actually fetched for. TargetCards read this
    // (not the tab's local state) so they never see a (targets, phase) pair that did not
    // come from the same response.
    targetsPhase: '',
    session: <any>{},
    trials: <any>{},
    sessionData: <any>{},
    sessionRunId: '',
    transcription: {
      segments: [] as TranscriptionSegment[],
      isRecording: false,
      isPaused: false,
      isPanelCollapsed: false,
      isPanelHidden: true,
      recordingStartTime: null as string | null,
      transcriptionName: '',
    },
  },
  error: false,
  // Mirrors targetsStatus — only the target grid gates the page loader.
  loading: false,
  targetsStatus: 'idle' as RequestStatus,
  sessionStatus: 'idle' as RequestStatus,
};

interface GetSession {
  sessionId: any;
  type: string;
}

interface RunSession {
  sessionId: string;
  createdBy: number;
  modifiedBy: number;
  startTime: string;
  modifiedDate?: string;
  isManualDataInsertion?: boolean;
  ranBy?: string;
}

interface GetSessionData {
  sessionId: string;
}

interface SaveTranscriptionPayload {
  sessionId: string;
  runSessionId: string;
  sessionDate: string;
  startTime: string;
  endTime: string;
  transcriptionText: string;
  transcriptionName: string;
}

export const getSessionTargets = createAsyncThunk(
  'getSessionCall',
  async (payload: GetSession, { rejectWithValue }) => {
    try {
      const res = await sessionApis.getSessionTargets(payload);
      return res.data.data;
    } catch (err) {
      rejectWithValue('Error in Response');
    }
  }
);

export const runSession = createAsyncThunk(
  'runSessionCall',
  async (payload: RunSession, { rejectWithValue }) => {
    try {
      const res = await sessionApis.runSession(payload);
      return {
        ...res.data.data,
        started: true,
      };
    } catch (err) {
      rejectWithValue('Error in Response');
    }
  }
);

export const getSession = createAsyncThunk(
  'getSessionDataCall',
  async (payload: GetSessionData, { rejectWithValue }) => {
    try {
      const res = await sessionApis.getSessionById(payload);
      return {
        ...res.data.data,
        started: true,
      };
    } catch (err) {
      rejectWithValue('Error in Response');
    }
  }
);

interface FetchTrialsArg {
  sessionRunId: string;
  target: any;
  phase: string;
  editMode?: boolean;
  // Resolved by the component from TimerProvider — the thunk stays free of React context.
  interactedTrial: number | null;
  force?: boolean;
  // A pinned target's trials are shared across every phase tab, so an empty response for one
  // tab means "not written under this phase yet", not "no data".
  isPinned?: boolean;
}

export const fetchTrialsForTarget = createAsyncThunk(
  'runSession/fetchTrialsForTarget',
  async (arg: FetchTrialsArg, { rejectWithValue }) => {
    const { sessionRunId, target, phase, editMode, interactedTrial } = arg;
    const key = makeTrialKey(sessionRunId, phase, target);
    const requestPayload = {
      sessionRunId,
      targetId: target.id,
      phase: getEffectivePhase(phase, target),
    };
    try {
      if (target?.targetType?.toLowerCase() === 'task analysis') {
        const res = await sessionApis.getTrialsByTargetForTaskAnalysis(requestPayload);
        return {
          key,
          ...shapeTaskAnalysisTrials({ responseData: res?.data?.data, phase, editMode }),
        };
      }
      const res = await sessionApis.getTrialsByTarget(requestPayload);
      return {
        key,
        ...shapeRegularTrials({
          responseData: res?.data?.data,
          target,
          phase,
          editMode,
          interactedTrial,
        }),
      };
    } catch (err) {
      return rejectWithValue({
        key,
        message: err instanceof Error ? err.message : 'Error fetching trials',
      });
    }
  },
  {
    // Cache read: a remount, a re-render or a duplicated StrictMode effect all abort here
    // before `pending` is dispatched, so no request is made at all.
    condition: (arg: FetchTrialsArg, { getState }) => {
      const state: any = getState();
      const entry: TrialEntry | undefined =
        state.runSession.value.trials[makeTrialKey(arg.sessionRunId, arg.phase, arg.target)];
      // A forced refresh always goes out, even mid-flight: it supersedes the in-flight
      // request, whose response is then dropped by the requestId check in `fulfilled`.
      if (arg.force) return true;
      return !entry || entry.status === 'idle' || entry.status === 'error';
    },
  }
);

interface SaveTrialArg {
  key: string;
  // Array index this save owns. Single source of truth for both the optimistic write
  // and the reconcile, so two concurrent saves can never clobber each other.
  index: number;
  trial: any;
  isTaskAnalysis?: boolean;
  // Pinned targets are collected across every phase tab and the backend returns their trials
  // whatever phase is queried, so the other tabs' cache entries go stale on each save.
  isPinned?: boolean;
  decoration: TrialSaveDecoration;
}

export const saveTrialThunk = createAsyncThunk(
  'runSession/saveTrial',
  async (arg: SaveTrialArg, { rejectWithValue }) => {
    const body = decorateTrialPayload(arg.trial, arg.decoration);
    try {
      const res = arg.isTaskAnalysis
        ? await sessionApis.saveTrialForTaskAnalysis(body)
        : await sessionApis.saveTrial(body);
      return { key: arg.key, index: arg.index, trialId: res?.data?.data?.id ?? '' };
    } catch (err) {
      return rejectWithValue({
        key: arg.key,
        index: arg.index,
        message: err instanceof Error ? err.message : 'Unknown error',
      });
    }
  }
);

interface UndoTrialArg {
  key: string;
  sessionId: string;
  sessionRunId: string;
  targetId: string;
  trialId: string;
  // Slot holding the trial being undone, and the slot currently on screen.
  undoIndex: number;
  currentIndex: number;
}

export const undoTrialThunk = createAsyncThunk(
  'runSession/undoTrial',
  async (arg: UndoTrialArg, { rejectWithValue }) => {
    try {
      const res = await sessionApis.undoTrial({
        sessionId: arg.sessionId,
        sessionRunId: arg.sessionRunId,
        targetId: arg.targetId,
        trialId: arg.trialId,
      });
      if (!res?.data?.data) {
        return rejectWithValue({ key: arg.key, message: 'Undo rejected by server' });
      }
      return { key: arg.key, undoIndex: arg.undoIndex, currentIndex: arg.currentIndex };
    } catch (err) {
      return rejectWithValue({
        key: arg.key,
        message: err instanceof Error ? err.message : 'Unknown error',
      });
    }
  }
);

export const saveTranscriptionThunk = createAsyncThunk(
  'saveTranscription',
  async (payload: SaveTranscriptionPayload, { rejectWithValue }) => {
    try {
      const res = await transcriptionApi.saveTranscription(payload);
      return res.data;
    } catch (err) {
      return rejectWithValue('Error saving transcription');
    }
  }
);

export const loadTranscriptionThunk = createAsyncThunk(
  'loadTranscription',
  async (runSessionId: string, { rejectWithValue }) => {
    try {
      const res = await transcriptionApi.getTranscriptionByRunSessionId(runSessionId);
      return {
        transcriptionText: res.data.data.transcriptionText,
        transcriptionName: res.data.data.transcriptionName || '',
      };
    } catch (err) {
      return rejectWithValue('Error loading transcription');
    }
  }
);

const runSessionSlice = createSlice({
  name: 'runSession',
  initialState,
  reducers: {
    setRunSessionData: (state, action) => {
      state.value = { ...action.payload };
    },
    removeAllSessionData: state => {
      state.value.targets = [];
      state.value.targetsPhase = '';
      state.value.trials = {};
      state.value.session = {};
      state.value.sessionData = {};
      state.value.sessionRunId = '';
    },
    pauseSession: state => {
      state.value.session['started'] = false;
    },
    // Move to a trial, seeding an empty slot if that trial has never been touched.
    //
    // This MUST be a reducer over current state rather than a caller-built array: cards
    // call it immediately after recordTrialResponse (e.g. Duration's record button records
    // trial N then advances to N+1), and a caller-built array is a render-time snapshot that
    // predates the optimistic save — writing it back erased the value just recorded.
    navigateToTrial: (state, action) => {
      const { key, trialCount, emptyTrial } = action.payload;
      const entry = ensureEntry(state, key);
      const index = trialCount - 1;
      if (index >= 0 && !entry.data[index]) {
        entry.data[index] = emptyTrial;
      }
      entry.currentTrial = trialCount;
    },
    clearJustUndone: (state, action) => {
      const entry = state.value.trials[action.payload.key];
      if (entry) entry.hasJustUndone = false;
    },
    // Explicit "Refresh": drop every cached phase for this run so the next render refetches.
    invalidateTrialsForRun: (state, action) => {
      Object.keys(state.value.trials).forEach(key => {
        if (isTrialKeyForRun(key, action.payload)) {
          delete state.value.trials[key];
        }
      });
    },
    resumeSession: state => {
      state.value.session['started'] = true;
    },
    sessionRunId: (state, action) => {
      state.value.sessionRunId = action.payload;
    },
    resumeExistingSession: (state, action) => {
      state.value.session = action.payload;
    },
    resetSession: state => {
      state.value = {
        targets: [],
        targetsPhase: '',
        session: <any>{},
        trials: <any>{},
        sessionData: <any>{},
        sessionRunId: '',
        transcription: {
          segments: [],
          isRecording: false,
          isPaused: false,
          isPanelCollapsed: false,
          isPanelHidden: true,
          recordingStartTime: null,
          transcriptionName: '',
        },
      };
    },
    // Transcription actions
    startTranscription: state => {
      state.value.transcription.isRecording = true;
      state.value.transcription.isPaused = false;
      state.value.transcription.recordingStartTime = new Date().toISOString();
    },
    pauseTranscription: state => {
      state.value.transcription.isPaused = true;
    },
    resumeTranscription: state => {
      state.value.transcription.isPaused = false;
    },
    stopTranscription: state => {
      state.value.transcription.isRecording = false;
      state.value.transcription.isPaused = false;
    },
    addTranscriptSegment: (state, action) => {
      const sessionTime = state.value.transcription.segments.length;
      state.value.transcription.segments.push({
        id: uuidv4(),
        text: action.payload.text,
        timestamp: action.payload.timestamp,
        sessionTime,
      });
    },
    updateSegmentText: (state, action) => {
      const segment = state.value.transcription.segments.find(seg => seg.id === action.payload.id);
      if (segment) {
        segment.text = action.payload.text;
      }
    },
    togglePanel: state => {
      state.value.transcription.isPanelCollapsed = !state.value.transcription.isPanelCollapsed;
    },
    hidePanel: state => {
      state.value.transcription.isPanelHidden = true;
    },
    showPanel: state => {
      state.value.transcription.isPanelHidden = false;
    },
    loadTranscriptFromText: (state, action) => {
      const paragraphs = action.payload.split('\n').filter((p: string) => p.trim());
      state.value.transcription.segments = paragraphs.map((text: string, index: number) => ({
        id: uuidv4(),
        text: text.trim(),
        timestamp: new Date().toISOString(),
        sessionTime: index,
      }));
    },
    clearTranscription: state => {
      state.value.transcription.segments = [];
      state.value.transcription.recordingStartTime = null;
      state.value.transcription.transcriptionName = '';
    },
  },
  extraReducers: builder => {
    builder
      .addCase(getSessionTargets.pending, state => {
        state.targetsStatus = 'loading';
        state.loading = true;
      })
      .addCase(getSessionTargets.fulfilled, (state: any, action) => {
        state.value.targets = action.payload;
        // Keep targets and the phase they belong to in lockstep.
        state.value.targetsPhase = action.meta.arg.type;
        state.targetsStatus = 'loaded';
        state.loading = false;
      })
      .addCase(getSessionTargets.rejected, state => {
        state.error = true;
        state.targetsStatus = 'error';
        state.loading = false;
      })
      .addCase(runSession.fulfilled, (state: any, action) => {
        state.value.session = action.payload;
      })
      // getSession only hydrates sessionData (breadcrumb name) — it must never gate the grid.
      .addCase(getSession.pending, state => {
        state.sessionStatus = 'loading';
      })
      .addCase(getSession.fulfilled, (state: any, action) => {
        state.value.sessionData = action.payload;
        state.sessionStatus = 'loaded';
      })
      .addCase(getSession.rejected, state => {
        state.error = true;
        state.sessionStatus = 'error';
      })
      .addCase(fetchTrialsForTarget.pending, (state: any, action) => {
        const entry = ensureEntry(
          state,
          makeTrialKey(action.meta.arg.sessionRunId, action.meta.arg.phase, action.meta.arg.target)
        );
        // Existing data is deliberately preserved so a background refresh never blanks a card.
        entry.status = 'loading';
        entry.requestId = action.meta.requestId;
      })
      .addCase(fetchTrialsForTarget.fulfilled, (state: any, action) => {
        const { key, data, currentTrial } = action.payload;
        const entry = ensureEntry(state, key);
        // A newer force-refetch already took over this key — drop this response.
        if (entry.requestId && entry.requestId !== action.meta.requestId) return;
        const isFrequency = action.meta.arg.target?.targetType?.toLowerCase() === 'frequency';
        // getTrialsByTarget filters by phase, so a target that appears in a phase with no
        // saved trial legitimately returns []. Clearing here would drop hasTrialId and let
        // collectZeroFrequency re-fire with frequency:0, overwriting the count saved under
        // another phase.
        const wouldClearSavedFrequency =
          isFrequency && !data.length && entry.data.some((t: any) => t.trialId || t.id);
        // Same rule, one step earlier and for every pinned target: this tab has nothing of
        // its own, so show what the other tabs hold instead of an empty card. The saved
        // mirror in saveTrialThunk.fulfilled only covers tabs already in the cache — this
        // covers the first visit to a tab, which is where the reset was reported.
        const sibling =
          !data.length && action.meta.arg.isPinned
            ? findSiblingTrials(state.value.trials, key)
            : undefined;
        if (sibling) {
          entry.data = sibling.data.map((row: any) => ({ ...row }));
          entry.currentTrial = sibling.currentTrial;
        } else if (!wouldClearSavedFrequency) {
          entry.data = data;
          entry.currentTrial = currentTrial;
        }
        entry.status = 'loaded';
        entry.fetchedAt = Date.now();
        entry.error = undefined;
      })
      .addCase(fetchTrialsForTarget.rejected, (state: any, action) => {
        const key = makeTrialKey(
          action.meta.arg.sessionRunId,
          action.meta.arg.phase,
          action.meta.arg.target
        );
        const entry = ensureEntry(state, key);
        if (entry.requestId && entry.requestId !== action.meta.requestId) return;
        entry.status = 'error';
        entry.error = 'Error fetching trials';
      })
      .addCase(saveTrialThunk.pending, (state: any, action) => {
        const { key, index, trial } = action.meta.arg;
        const entry = ensureEntry(state, key);
        // Optimistic: patch only this index, never replace the array. Two saves in flight
        // against different trials must not clobber one another.
        entry.data[index] = { ...trial };
        entry.saving += 1;
      })
      .addCase(saveTrialThunk.fulfilled, (state: any, action) => {
        const { key, index, trialId } = action.payload;
        const entry = ensureEntry(state, key);
        entry.saving = Math.max(0, entry.saving - 1);
        entry.data[index] = { ...entry.data[index], trialId, saveFailed: false };
        entry.hasJustUndone = false;
        // A pinned target holds the same trials in every phase, so hand the saved value
        // straight to the other tabs' entries. Invalidating them instead would be correct but
        // visibly worse: the next visit would render 0 until the refetch landed, which reads
        // as a data reset. `current` snapshots the draft so the entries stay independent.
        if (action.meta.arg.isPinned) {
          const snapshot = current(entry);
          Object.keys(state.value.trials).forEach((other: string) => {
            if (!isSiblingPhaseKey(other, key)) return;
            const sibling = state.value.trials[other];
            sibling.data = snapshot.data.map((row: any) => ({ ...row }));
            sibling.currentTrial = snapshot.currentTrial;
            sibling.status = 'loaded';
            sibling.fetchedAt = Date.now();
          });
        }
      })
      .addCase(saveTrialThunk.rejected, (state: any, action) => {
        const { key, index } = action.meta.arg;
        const entry = ensureEntry(state, key);
        entry.saving = Math.max(0, entry.saving - 1);
        // The optimistic value is kept on purpose: a clinician's tap staying visible with a
        // failure affordance beats it silently vanishing, which is the reported symptom.
        if (entry.data[index]) entry.data[index].saveFailed = true;
      })
      .addCase(undoTrialThunk.pending, (state: any, action) => {
        ensureEntry(state, action.meta.arg.key).saving += 1;
      })
      .addCase(undoTrialThunk.fulfilled, (state: any, action) => {
        const { key, undoIndex, currentIndex } = action.payload;
        const entry = ensureEntry(state, key);
        entry.saving = Math.max(0, entry.saving - 1);
        const prevTrial = undoIndex > 0 ? entry.data[undoIndex - 1] : null;
        if (entry.data[undoIndex]) {
          entry.data[undoIndex] = {
            ...entry.data[undoIndex],
            trialId: '',
            id: '',
            positiveCount: '',
            negativeCount: '',
          };
        }
        // Write the previous trial's values into the visible slot. No trialId means
        // useLazyEffect will still auto-advance on the next recording.
        if (entry.data[currentIndex]) {
          entry.data[currentIndex] = {
            ...entry.data[currentIndex],
            positiveCount: prevTrial?.positiveCount || '',
            negativeCount: prevTrial?.negativeCount || '',
          };
        }
        entry.hasJustUndone = true;
      })
      .addCase(undoTrialThunk.rejected, (state: any, action) => {
        const entry = ensureEntry(state, action.meta.arg.key);
        entry.saving = Math.max(0, entry.saving - 1);
      })
      .addCase(loadTranscriptionThunk.fulfilled, (state: any, action) => {
        if (action.payload) {
          const paragraphs = action.payload.transcriptionText
            .split('\n')
            .filter((p: string) => p.trim());
          state.value.transcription.segments = paragraphs.map((text: string, index: number) => ({
            id: uuidv4(),
            text: text.trim(),
            timestamp: new Date().toISOString(),
            sessionTime: index,
          }));
          state.value.transcription.transcriptionName = action.payload.transcriptionName || '';
        }
      })
      .addCase(loadTranscriptionThunk.rejected, state => {
        // Silently fail if no transcription exists
        console.log('No transcription found for this session');
      });
  },
});

export const {
  setRunSessionData,
  removeAllSessionData,
  pauseSession,
  navigateToTrial,
  clearJustUndone,
  invalidateTrialsForRun,
  resumeSession,
  resumeExistingSession,
  resetSession,
  startTranscription,
  pauseTranscription,
  resumeTranscription,
  stopTranscription,
  addTranscriptSegment,
  updateSegmentText,
  togglePanel,
  hidePanel,
  showPanel,
  loadTranscriptFromText,
  clearTranscription,
} = runSessionSlice.actions;

export default runSessionSlice.reducer;
